import * as Inovx from '@shared';
import KnowledgeViewServices from '../services/KnowledgeViewServices';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import ViewerStore from '../stores/ViewerStore';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var FormHelper = Inovx.Shared.Common.FormHelper;
import KnowledgeBookConstants from './../constants/KnowledgeBookConstants';
import ViewerConstants from './../constants/ViewerConstants';
import KnowledgeBookServices from './../services/KnowledgeBookServices';
import AssetSetsServices from './../services/AssetSetsServices';
import {
    buildInitialKnowledgeViewDraft,
    getOntologyScopedScanPositions,
    isPanoFirstViewAvailable,
    NewKnowledgeBookCreationInput,
    NewKnowledgeBookCreationResult,
} from '../components/knowledgeLibrary/knowledgeBookCreationModel';

interface CreateKnowledgeViewOptions {
    initializeDefaultAssetSets?: boolean;
    refreshStores?: boolean;
}

interface UpdateKnowledgeViewOptions {
    refreshStore?: boolean;
}

const CREATE_KNOWLEDGE_BOOK_FROM_LIBRARY = 'CREATE_KNOWLEDGE_BOOK_FROM_LIBRARY';

function _sendInvalidKBSave() {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_INVALID_ADD
    });
}
function _sendInvalidKVSave() {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_INVALID_ADD
    });
}

function _receiveFacilityKnowledgeBooks(data: IKnowledgeBook[]): void {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_GET_ALL,
        data: data
    });
}
function _receiveKnowledgeViews(data: IKnowledgeView[]): void {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_GET_ALL,
        data: data
    });
}
function _receiveAssetSets(data: IAssetSet[]): void {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.ASSET_SETS_GET_ALL,
        data: data
    });
}
function _finishEdit() {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FINISH_EDIT
    });
}
function _finishKVEdit() {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_FINISH_EDIT
    });
}

function _setKnowledgeBook(kb: IKnowledgeBook) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_DETAILS,
        data: kb
    });
}
function _receiveFavoriteKnowledgeBookIds(data: string[]): void {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITES_GET_ALL,
        data: data
    });
}
function _beginAssignClassificationTags(knowledgeBooks: IKnowledgeBook[], selectedTagIds: string[] = []) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSIGN_TAGS_BEGIN,
        data: {
            knowledgeBooks,
            selectedTagIds,
        }
    });
}
function _updateAssignClassificationTags(selectedTagIds: string[]) {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSIGN_TAGS_UPDATE,
        data: {
            selectedTagIds,
        }
    });
}
function _finishAssignClassificationTags() {
    Inovx.Shared.Flux.AppDispatcher.dispatch({
        actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ASSIGN_TAGS_FINISH
    });
}

function _getValidClassificationTags(classificationTags?: string[]) {
    const taxonomyDetail = FacilityStore.getVisibleTaxonomyClassificationDetail();
    const validTagIds = new Set((taxonomyDetail?.ClassificationTags ?? []).map(tag => tag.ID));

    return [...new Set((classificationTags ?? []).filter(tagId => !!tagId && validTagIds.has(tagId)))];
}

function getAnnotationType(assetClassName: string) {
    if (assetClassName === "ObservationTag") {
        return AnnotationType.ObservationTag;
    } else if (assetClassName === "InformationAnnotation") {
        return AnnotationType.InformationAnnotation;
    } else if (assetClassName === "LinkAnnotation") {
        return AnnotationType.LinkAnnotation;
    } else {
        return AnnotationType.ObservationTag;
    }
}

var KnowledgeBookActions = {
    async refreshCurrentKnowledgeBooks() {
        const facility = FacilityStore.getSelectedFacility()
        return await KnowledgeBookActions.getFacilityKnowledgeBooks(facility.ID);
    },

    async getFacilityKnowledgeBooks(facilityID: string) {
        const data = await KnowledgeBookServices.getFacilityKnowledgeBooks(facilityID);
        const taxonomyDetail = FacilityStore.getVisibleTaxonomyClassificationDetail();

        // Validate that the classification tags used in knowledge books still exist in the facility taxonomy
        data.forEach((kb) => {
            kb.ClassificationTags = (kb.ClassificationTags || []).filter((tagID) => {
                return taxonomyDetail?.ClassificationTags?.some((ct) => ct.ID === tagID);
            });
        });

        _receiveFacilityKnowledgeBooks(data);
        return data;
    },
    async getKnowledgeViews(knowledgeBookID: string) {
        const data = await KnowledgeViewServices.getKnowledgeViews(knowledgeBookID);
        _receiveKnowledgeViews(data);
        return data;
    },
    async resetKnowledgeViews() {
        _receiveKnowledgeViews([]);
    },
    async getKnowledgeBookData(kbId: string) {
        const data = await KnowledgeBookServices.getKnowledgeBookData(kbId);
        return data;
    },
    async getKnowledgeBook(kbId: string) {
        if(kbId) {
            const data = await KnowledgeBookServices.getKnowledgeBook(kbId);
            return data;
        }
        return null;
    },
    async getFavoriteKnowledgeBookIds() {
        const data = await KnowledgeBookServices.getFavoriteKnowledgeBookIds();
        _receiveFavoriteKnowledgeBookIds(data);
        return data;
    },
    async toggleFavoriteKnowledgeBook(kb: IKnowledgeBook) {
        if (!kb?.ID) return;

        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE);
            try {
                if (KnowledgeBookStore.isFavoriteKnowledgeBook(kb.ID)) {
                    await KnowledgeBookServices.removeFavoriteKnowledgeBook(kb.ID);
                } else {
                    await KnowledgeBookServices.addFavoriteKnowledgeBook(kb.ID);
                }
                await KnowledgeBookActions.getFavoriteKnowledgeBookIds();
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE);
            }
        }
    },
    async addFavoritesForKnowledgeBooks(kbs: IKnowledgeBook[]) {
        const ids = kbs.map(kb => kb.ID).filter(Boolean);
        if (ids.length === 0) return;

        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE);
            try {
                await KnowledgeBookServices.batchUpdateFavoriteKnowledgeBooks(ids, true);
                await KnowledgeBookActions.getFavoriteKnowledgeBookIds();
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE);
            }
        }
    },
    async removeFavoritesForKnowledgeBooks(kbs: IKnowledgeBook[]) {
        const ids = kbs.map(kb => kb.ID).filter(Boolean);
        if (ids.length === 0) return;

        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE);
            try {
                await KnowledgeBookServices.batchUpdateFavoriteKnowledgeBooks(ids, false);
                await KnowledgeBookActions.getFavoriteKnowledgeBookIds();
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_FAVORITE_UPDATE);
            }
        }
    },
    openAssignClassificationTagsForm(kbs: IKnowledgeBook[]) {
        const knowledgeBooks = (kbs || []).filter(kb => !!kb?.ID);
        if (knowledgeBooks.length === 0) return;

        _beginAssignClassificationTags(knowledgeBooks);
    },
    updateAssignClassificationTagsSelection(selectedTagIds: string[]) {
        _updateAssignClassificationTags(_getValidClassificationTags(selectedTagIds));
    },
    closeAssignClassificationTagsForm() {
        _finishAssignClassificationTags();
    },
    async assignClassificationTagsToKnowledgeBooks(replaceExisting: boolean) {
        const knowledgeBooks = KnowledgeBookStore.getAssignClassificationTagsKnowledgeBooks();
        const ids = knowledgeBooks.map(kb => kb.ID).filter(Boolean);
        const versionTokens = knowledgeBooks.reduce<Record<string, string>>((tokens, knowledgeBook) => {
            if (knowledgeBook.ID) {
                tokens[knowledgeBook.ID] = knowledgeBook.VersionToken;
            }
            return tokens;
        }, {});
        if (ids.length === 0) {
            _finishAssignClassificationTags();
            return;
        }

        const selectedTagIds = _getValidClassificationTags(KnowledgeBookStore.getAssignClassificationTagsSelectedTagIds());

        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_CLASSIFICATION_TAGS_UPDATE)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_CLASSIFICATION_TAGS_UPDATE);
            try {
                await KnowledgeBookServices.batchUpdateClassificationTags(ids, selectedTagIds, replaceExisting, versionTokens);
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
                _finishAssignClassificationTags();
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_CLASSIFICATION_TAGS_UPDATE);
            }
        }
    },
    async openKnowledgeBook(kb: IKnowledgeBook) {
        _setKnowledgeBook(kb);
        const data = await KnowledgeBookServices.getKnowledgeBookData(kb.ID);
        _receiveKnowledgeViews(data.KnowledgeViews);
        _receiveAssetSets(data.AssetSets);
        return data;
    },
    closeKnowledgeBook() {
        _setKnowledgeBook(null);
    },

    showKnowledgeBookDescription(status: boolean) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.SHOW_KNOWLEDGE_BOOK_DESCRIPTION,
            status
        });
    },
    showEmptyKnowledgeViewMessage() {
        SystemActions.displayInfoMessage({
            message: InovxLoader.t('facility:message.emptyKnowledgeViewMessage'),
        });
        
    },
    setKnowledgeBookViewingMode(mode: KnowledgeBookViewingMode) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_VIEWING_MODE,
            mode
        });
    },
    beginAdd(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_ADD
        });
    },
    beginEdit(data: IKnowledgeBook): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT,
            data
        });
    },
    updateKnowledgeBook(data: IKnowledgeBook): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_UPDATE,
            data
        });
    },
    beingFinish(): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_BOOK_FINISH_EDIT
        });
    },
    finishEdit(): void {
        if (KnowledgeBookStore.getHasChanges()) {
            var callbackFunct = KnowledgeBookStore.getIsEditing() ? KnowledgeBookActions.editKnowledgeBook : KnowledgeBookActions.createKnowledgeBook;
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: callbackFunct,
                cancelCallback: _finishEdit
            });
        } else {
            _finishEdit();
        }
    },
    beginEditKnowledgeView(data: IKnowledgeView): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_BEING_EDIT,
            data
        });
    },
    finishKnowledgeViewEdit(): void {
        if (KnowledgeBookStore.getHasChanges()) {
            var callbackFunct = KnowledgeBookActions.setKnowledgeViewProperties;
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: callbackFunct,
                cancelCallback: _finishKVEdit
            });
        } else {
            _finishKVEdit();
        }
    },
    updateKnowledgeViewInStore(data: IKnowledgeView): void {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_UPDATE,
            data
        });
    },
    async getKnowledgeViewData(id: string) {
        let kvDetail = await KnowledgeViewServices.getKnowledgeView(id);
        return kvDetail;
    },

    async getKnowledgeViewDetail(kvID: string) {
        const kvDetailResponse = await KnowledgeViewServices.getKnowledgeViewDetail(kvID);

        const displayProperties = JSON.parse(kvDetailResponse.DisplayProperties) as IDisplayProperties;
        const kvDetail: IKnowledgeViewDetail = {...kvDetailResponse, DisplayProperties: displayProperties};

        kvDetail.View = FacilityStore.getSelectedFacility().Views.find(v => v.ID == kvDetail.ViewID);
        const facility = FacilityStore.getSelectedFacility();
        kvDetail.Tags?.forEach((ann) => {
            ann.AssetClass = facility.AnnotationClasses.find(o => o.ID === ann.AssetClassID);
            ann.AnnotationType = getAnnotationType(ann.AssetClass.Name);
        });
        kvDetail.AssetSets = kvDetail.AssetSets || [];
        return kvDetail;
    },
    async getSingleKnowledgeView(kvID: string) {
        const kv = await KnowledgeViewServices.getKnowledgeView(kvID);
         Inovx.Shared.Flux.AppDispatcher.dispatch({
             actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_LIST_UPDATE,
             data: kv
         });
        return kv;
    },
    async refreshKnowledgeViews(knowledgeBookID: string) {
        // First refresh all knowledge views for the knowledge book
        await KnowledgeBookActions.getKnowledgeViews(knowledgeBookID);

        // Then refresh the selected knowledge view detail
        const selectedKV = ViewerStore.getSelectedKnowledgeView();
        if (selectedKV) {
            const kvDetail = await KnowledgeBookActions.getKnowledgeViewDetail(selectedKV.ID);

            // This is a hack - we should not be dispatching a ViewerStore event from here.
            // However, if we'd try to invoke a ViewerAction method instead, we'd run into a circular dependency issue.
            Inovx.Shared.Flux.AppDispatcher.dispatch({
                actionType: ViewerConstants.KNOWLEDGE_VIEW_OPEN,
                data: {kv: kvDetail}
            });
        }
    },
    async renameKnowledgeView(kv: IKnowledgeView) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: ViewerConstants.KNOWLEDGE_VIEW_RENAME,
            data: kv
        });        
    },
    async updateKnowledgeViews(kv: IKnowledgeView) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.KNOWLEDGE_VIEW_UPDATE_ALL,
            data: kv
        });
    },
    async createKnowledgeView(kv: IKnowledgeViewDetail, options: CreateKnowledgeViewOptions = {}) {
        const initializeDefaultAssetSets = options.initializeDefaultAssetSets ?? true;
        const refreshStores = options.refreshStores ?? true;
        let data = FormHelper.trim(kv);
        var form_data: any = new FormData();
        form_data.append("Name", data.Name)
        form_data.append("Description", data.Description || '')
        form_data.append("KnowledgeBookID", data.KnowledgeBookID)
        if (data.ViewID) form_data.append("ViewID", data.ViewID)
        form_data.append("Type", data.Type);
        if (data.OntologyID) form_data.append("OntologyID", data.OntologyID);
        form_data.append("DisplayProperties", JSON.stringify(data.DisplayProperties));
        if (data.ThumbnailImageFile) form_data.append("ThumbnailImageFile", data.ThumbnailImageFile, "thumbnail.png");

        const kvRes = await KnowledgeViewServices.createKnowledgeView(form_data);

        //save All 3D Assets if created in 3D viewer and Assets content layer selected
        const layers = ViewerStore.getContentLayers();
        const isAssetsMeshOn = layers.assets || layers.mesh;
        if (initializeDefaultAssetSets && isAssetsMeshOn && kvRes.Type === 2) {
            const assetSets = KnowledgeBookStore.getAllAssetSet();
            const all3DAsset = assetSets.find((assetSet: IAssetSet) => assetSet.Type === AssetSetType.All3DAssets);
            if (all3DAsset) {
                let kvDetails = await KnowledgeBookActions.getKnowledgeViewDetail(kvRes.ID)
                const assetSet: IKnowledgeViewAssetSet[] = [{ ID: all3DAsset.ID, Ord: 1}]
                kvDetails.AssetSets = assetSet;
                await KnowledgeBookActions.updateKnowledgeView(kvDetails)
            }
        }
        // refresh data
        if (refreshStores) {
            await KnowledgeBookActions.refreshKnowledgeViews(data.KnowledgeBookID);
            await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
        }

        return kvRes;
    },

    async updateKnowledgeView(kv: IKnowledgeViewDetail, options: UpdateKnowledgeViewOptions = {}) {
        //let data = FormHelper.trim(kv);
        var form_data: any = new FormData();
        form_data.append("ID", kv.ID)
        form_data.append("Name", kv.Name)
        form_data.append("Description", kv.Description || '')
        form_data.append("VersionToken", kv.VersionToken)
        form_data.append("Type", kv.Type)
        form_data.append("KnowledgeBookID", kv.KnowledgeBookID)
        kv.OntologyID && form_data.append("OntologyID", kv.OntologyID)
        kv.ViewID && form_data.append("ViewID", kv.ViewID)
        form_data.append("DisplayProperties", JSON.stringify(kv.DisplayProperties))
        kv.AssetSets?.forEach((item, index) => {
            form_data.append(`AssetSets[${index}].ID`, item.ID);
            form_data.append(`AssetSets[${index}].Ord`, item.Ord.toString());
            item.Color != null && form_data.append(`AssetSets[${index}].Color`, item.Color.toString());
            form_data.append(`AssetSets[${index}].Hidden`, item.Hidden ? 'true' : 'false');
            item.MaterialID && form_data.append(`AssetSets[${index}].MaterialID`, item.MaterialID);
        });
        if (kv.ThumbnailImageFile) form_data.append("ThumbnailImageFile", kv.ThumbnailImageFile, "thumbnail.png");

        let kvRes = await KnowledgeViewServices.updateKnowledgeViewDetails(form_data);

        // refresh data
        if (options.refreshStore ?? true) await KnowledgeBookActions.getSingleKnowledgeView(kv.ID)
        return kvRes;
    },

    async setKnowledgeViewProperties(kv: IKnowledgeView = null) {
        if (KnowledgeBookStore.getIsValidObject()) {
            if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_VIEW_EDIT)) {
                SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_VIEW_EDIT);
                try {
                    if (!kv) {
                        kv = KnowledgeBookStore.getKnowledgeViewDetail()
                    }
                    let data = FormHelper.trim(kv);
                    var form_data: any = new FormData();
                    form_data.append("ID", data.ID)
                    form_data.append("VersionToken", data.VersionToken)
                    form_data.append("Name", data.Name)
                    form_data.append("Description", data.Description || '')

                    const updatedKV = await KnowledgeViewServices.setKnowledgeViewProperties(form_data);
                    // refresh data
                    await KnowledgeBookActions.renameKnowledgeView(updatedKV)
                    await KnowledgeBookActions.updateKnowledgeViews(updatedKV)
                    _finishKVEdit();
                }
                finally {
                    SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_VIEW_EDIT);
                }
            } else {
                _sendInvalidKVSave();
            }
        } else {
            _sendInvalidKVSave()
        }
        
    },

    async deleteKnowledgeView(kv: IKnowledgeView) {
        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT);
            try {
                await KnowledgeViewServices.deleteKnowledgeView(kv);

                // refresh data
                await KnowledgeBookActions.refreshKnowledgeViews(kv.KnowledgeBookID)
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT);
            }
        } else {
            _sendInvalidKBSave();
        }
    },

    async createKnowledgeBook() {
        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_ADD)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_ADD);

            try {
                const selectedFacility = FacilityStore.getSelectedFacility();
                const knowledgeBook = KnowledgeBookStore.getKnowledgeBook();
                const kb: IKnowledgeBook = {
                    ID: null,
                    VersionToken: null,
                    Name: knowledgeBook.Name,
                    FacilityID: selectedFacility.ID,
                    Description: knowledgeBook.Description,
                    ClassificationTags: knowledgeBook.ClassificationTags,
                }

                if (ViewerStore.is3DViewerVisible()) {
                    const ontology = FacilityStore.getSelectedOntology();
                    kb.OntologyID = ontology.ID;
                }

                const kbRes = await KnowledgeBookServices.createKnowledgeBook(kb);
                // refresh data
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();               
                _finishEdit();
                
                return kbRes;
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_ADD);
            }
        }
        else {
            _sendInvalidKBSave();
        }
        
    },

    async createKnowledgeBookFromLibrary(input: NewKnowledgeBookCreationInput): Promise<NewKnowledgeBookCreationResult> {
        const facility = FacilityStore.getSelectedFacility();
        const normalizedName = input.name.trim();
        const classificationTags = [...new Set((input.classificationTags ?? []).filter(Boolean))];
        const ontology = facility?.OntologyParts?.find(part => part.ID === input.ontologyId);
        const scopedScanPositions = getOntologyScopedScanPositions(facility, input.ontologyId);
        const panoAvailable = isPanoFirstViewAvailable(ontology, scopedScanPositions);

        if (!facility?.ID || !normalizedName || !ontology || classificationTags.length === 0
            || (input.firstView !== '3d' && input.firstView !== 'pano')
            || (input.firstView === 'pano' && (!panoAvailable || !scopedScanPositions.some(view => view.ID === input.scanPositionId)))) {
            throw new Error('Invalid new Knowledge Book input.');
        }

        if (SystemStore.isActionInProgress(CREATE_KNOWLEDGE_BOOK_FROM_LIBRARY)) {
            throw new Error('Knowledge Book creation is already in progress.');
        }

        SystemActions.beginAction(CREATE_KNOWLEDGE_BOOK_FROM_LIBRARY);
        try {
            const knowledgeBook: IKnowledgeBook = {
                ID: null,
                VersionToken: null,
                Name: normalizedName,
                FacilityID: facility.ID,
                Description: input.description?.trim() || '',
                OntologyID: input.ontologyId,
                ClassificationTags: classificationTags,
            };
            const createdKnowledgeBook = await KnowledgeBookServices.createKnowledgeBook(knowledgeBook);
            let initialView: IKnowledgeView = null;
            let initialViewError: unknown;

            try {
                const viewName = input.firstView === '3d'
                    ? InovxLoader.t('facility:assetSetVisualization.default3DKnowledgeViewName')
                    : InovxLoader.t('facility:assetSetVisualization.defaultPanoKnowledgeViewName').replace(
                        '__view__',
                        facility.Views?.find(view => view.ID === input.scanPositionId)?.Name
                            || InovxLoader.t('facility:knowledgeBook.pano')
                    );
                const viewDraft = buildInitialKnowledgeViewDraft(input, createdKnowledgeBook, viewName);
                initialView = await KnowledgeBookActions.createKnowledgeView(viewDraft, {
                    initializeDefaultAssetSets: false,
                    refreshStores: false,
                });

                // if (input.firstView === '3d' && initialView?.ID) {
                //     const facilityAssetSets = await AssetSetsServices.getAssetSetsByFacility(facility.ID);
                //     const all3DAssets = facilityAssetSets.find(assetSet => assetSet.Type === AssetSetType.All3DAssets);
                //     if (all3DAssets) {
                //         const viewDetail = await KnowledgeBookActions.getKnowledgeViewDetail(initialView.ID);
                //         viewDetail.AssetSets = [{ ID: all3DAssets.ID, Ord: 1 }];
                //         await KnowledgeBookActions.updateKnowledgeView(viewDetail, { refreshStore: false });
                //     }
                // }
            } catch (error) {
                initialViewError = error;
                console.error('The Knowledge Book was created without its initial Knowledge View.', error);
            }

            await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
            return { knowledgeBook: createdKnowledgeBook, initialView, initialViewError };
        } finally {
            SystemActions.endAction(CREATE_KNOWLEDGE_BOOK_FROM_LIBRARY);
        }
    },

    async editKnowledgeBook(kb: IKnowledgeBook = null) {
        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT);
            try {
                let knowledgeBook = kb; 
                if (!kb) {
                    knowledgeBook = KnowledgeBookStore.getKnowledgeBook();
                }
                const kbRes = await KnowledgeBookServices.editKnowledgeBook(knowledgeBook);

                // refresh data
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
                _finishEdit();

                return kbRes;
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT);
            }
        } else {
            _sendInvalidKBSave();
        }
    },

    async deleteKnowledgeBook(kb: IKnowledgeBook) {
        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_DELETE)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_DELETE);
            try {
                await KnowledgeBookServices.deleteKnowledgeBook(kb);
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_DELETE);
            }
        }
    },

    async batchDeleteKnowledgeBooks(kbs: IKnowledgeBook[]) {
        const ids = kbs.map(kb => kb.ID).filter(Boolean);
        if (ids.length === 0) return { HasPartialFailure: false };

        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_DELETE)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_DELETE);
            try {
                const result = await KnowledgeBookServices.batchDeleteKnowledgeBooks(ids);
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
                await KnowledgeBookActions.getFavoriteKnowledgeBookIds();
                return result;
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_DELETE);
            }
        }

        return { HasPartialFailure: false };
    },

    async setKnowledgeViewOrder(kb: IKnowledgeBook, kvList: string[]) {
        if (!SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT)) {
            SystemActions.beginAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT);
            try {
                await KnowledgeBookServices.setKnowledgeViewOrder(kb, kvList);
                await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
                await KnowledgeBookActions.getKnowledgeViews(kb.ID)
            }
            finally {
                SystemActions.endAction(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT);
            }
        }
    },
    pinUnpinKBPopup(status: boolean) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: KnowledgeBookConstants.PIN_UNPIN_KB_POPUP,
            status: status
        });
    }
}

export default KnowledgeBookActions;
